import { useState } from 'react'
import { Link } from 'react-router-dom'

import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import type { HiredCandidate } from '@/features/recruiter/api/hires.api'
import { CandidateDetailDrawer } from '@/features/recruiter/components/CandidateDetailDrawer'
import { useRecruiterHiresQuery } from '@/features/recruiter/hooks/useHires'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { formatDate } from '@/utils/format'

export function HiredCandidatesPage() {
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null)

  const debouncedQ = useDebouncedValue(q, 400)
  const { data, isPending, isFetching } = useRecruiterHiresQuery({
    q: debouncedQ.trim() || undefined,
    page,
    per_page: 10,
  })

  const columns: DataTableColumn<HiredCandidate>[] = [
    {
      key: 'candidate',
      header: 'Candidate',
      render: (row) => (
        <div>
          <p className="fs-13 fw-semibold text-primary mb-0">{row.candidateName ?? '—'}</p>
          <p className="fs-12 text-muted mb-0">{row.candidateEmail ?? ''}</p>
        </div>
      ),
    },
    {
      key: 'job',
      header: 'Hired For',
      render: (row) => (
        <Link to={`/recruiter/jobs/${row.jobId}/applicants`} onClick={(e) => e.stopPropagation()}>
          {row.jobTitle}
        </Link>
      ),
    },
    {
      key: 'appliedAt',
      header: 'Applied',
      render: (row) => <span className="fs-12 text-muted text-nowrap">{formatDate(row.appliedAt)}</span>,
    },
    {
      key: 'hiredAt',
      header: 'Hired On',
      render: (row) => (
        <span className="badge bg-success-transparent fs-12">
          <i className="ti ti-trophy me-1" />
          {formatDate(row.hiredAt)}
        </span>
      ),
    },
  ]

  return (
    <>
      <div className="my-4 d-flex align-items-center justify-content-between flex-wrap gap-2 page-header-breadcrumb">
        <div>
          <p className="fw-semibold fs-20 mb-1">Hired Candidates</p>
          <p className="text-muted fs-13 mb-0">
            {data?.meta
              ? `${data.meta.total.toLocaleString()} candidate${data.meta.total === 1 ? '' : 's'} hired by your organisation`
              : 'Everyone your organisation has marked as hired.'}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isFetching && !isPending && (
            <span className="spinner-border spinner-border-sm text-muted" role="status" aria-label="Loading" />
          )}
          <div className="input-group input-group-sm" style={{ minWidth: 260 }}>
            <span className="input-group-text bg-white">
              <i className="ti ti-search fs-13 text-muted" />
            </span>
            <input
              type="text"
              className="form-control fs-13"
              placeholder="Search name, email or job…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value)
                setPage(1)
              }}
              aria-label="Search hired candidates"
            />
          </div>
        </div>
      </div>

      <div className="card custom-card">
        <DataTable
          columns={columns}
          rows={data?.hires ?? []}
          rowKey={(row) => row.applicationId}
          loading={isPending}
          emptyMessage={q ? 'No hired candidates match this search.' : 'No candidates have been marked as hired yet.'}
          pagination={data?.meta}
          onPageChange={setPage}
          onRowClick={(row) => setSelectedProfileId(row.studentProfileId)}
        />
      </div>

      <CandidateDetailDrawer
        show={selectedProfileId != null}
        onClose={() => setSelectedProfileId(null)}
        studentProfileId={selectedProfileId ?? undefined}
      />
    </>
  )
}
